import React, { useState } from 'react'; // Import useState
import { useSoWForm } from '../../hooks/useSoWForm';
import FormField from '../../components/FormField/FormField';
import ActionBar from '../../components/ActionBar/ActionBar';
import ExpandableModal from '../../components/ExpandableModal/ExpandableModal'; // Import ExpandableModal
import PreviewModal from '../../components/PreviewModal/PreviewModal'; // Import PreviewModal
import { importJsonFile } from '../../lib/jsonUtils';
import { base64ToBlob } from '../../lib/fileUtils'; // Import the new utility

import styles from './SoWEditor.module.css';

const TABS_CONFIG = [
  {
    id: 'foundation',
    label: 'Project Foundation & Scope',
    fields: [
      'project_title_and_document_information',
      'parties_involved',
      'project_objectives_and_business_drivers',
      'scope_of_work',
      'in_scope_modules_and_functionality',
      'out_of_scope_items',
      'assumptions'
    ]
  },
  {
    id: 'execution',
    label: 'Execution & Strategy',
    fields: [
      'project_approach_and_methodology',
      'project_phases_and_milestones',
      'deliverables',
      'project_timeline___schedule',
      'roles_and_responsibilities_client_and_vendor_partner',
      'project_governance_and_escalation_process',
      'resource_plan_client_and_implementation_partner',
      'change_request_management'
    ]
  },
  {
    id: 'technical',
    label: 'Technical & Operational Plans',
    fields: [
      'testing_strategy',
      'data_migration_approach',
      'integration_approach',
      'customizations_and_reports_ricefw',
      'training_strategy_and_plan',
      'cutover_and_go_live_plan',
      'hypercare___post_go_live_support'
    ]
  },
  {
    id: 'compliance',
    label: 'Compliance & Commercials',
    fields: [
      'acceptance_criteria',
      'payment_terms_and_milestones',
      'pricing_and_commercial_terms',
      'risks_and_mitigation',
      'terms_and_conditions',
      'signatures___approval'
    ]
  }
];

const SoWEditor = () => {
  const LAMBDA_FUNCTION_URL = "https://53uvloqwozv6ziyzsgi2mt67iu0xmwuh.lambda-url.us-west-1.on.aws/";
  const { 
    formData, 
    handleFieldChange, 
    handleLockToggle, 
    updateAllFormData, 
    getExportData,
    isLoading,
    startLoading,
    stopLoading       
  } = useSoWForm();

  const [selectedTemplateFile, setSelectedTemplateFile] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false); // State for modal visibility
  const [editingFieldKey, setEditingFieldKey] = useState(null); // State for which field is being edited
  const [activeTab, setActiveTab] = useState(TABS_CONFIG[0].id);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [markdownContent, setMarkdownContent] = useState('');

  const handleImport = async (file) => {
    startLoading();
    try {
      if (file) {
        const jsonData = await importJsonFile(file);
        updateAllFormData(jsonData);
        alert('JSON imported successfully!');
      }
    } catch (error) {
      alert(error.message);
      console.error("JSON import error:", error);
    } finally {
      stopLoading();
    }
  };

  const handleExport = () => {
    startLoading();
    try {
      const dataToExport = getExportData();
      const jsonString = JSON.stringify(dataToExport, null, 2);

      let fileName = prompt("Enter filename for export:", "sow_data.json");
      if (fileName === null) {
        stopLoading();
        return; // User cancelled
      }
      if (!fileName.toLowerCase().endsWith('.json')) {
        fileName += '.json';
      }

      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      alert('JSON exported successfully!');
    } catch (error) {
      alert('Error exporting JSON: ' + error.message);
      console.error("JSON export error:", error);
    } finally {
      stopLoading();
    }
  };

  const handleTemplateFileSelect = (file) => {
    setSelectedTemplateFile(file);
    alert(`Template selected: ${file ? file.name : 'None'}`);
  };

  const generateMarkdownContent = () => {
    let md = '# Statement of Work Preview\n\n';
    
    TABS_CONFIG.forEach(tab => {
      md += `## ${tab.label}\n\n`;
      tab.fields.forEach(key => {
        const field = formData[key];
        if (field) {
          const label = key.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
          md += `### ${label}\n\n`;
          // If value is empty, show a placeholder or nothing
          md += `${field.value ? field.value : '*(No content provided)*'}\n\n`;
        }
      });
      md += '---\n\n'; // Separator between sections
    });
    
    return md;
  };

  const handlePreview = () => {
    const content = generateMarkdownContent();
    setMarkdownContent(content);
    setIsPreviewModalOpen(true);
  };

  const handleGenerateDocument = async () => {
    startLoading();
    try {
      if (!selectedTemplateFile) {
        alert('Please select a DOCX template file first!');
        return;
      }

      // Read the DOCX file as ArrayBuffer
      const reader = new FileReader();
      reader.readAsArrayBuffer(selectedTemplateFile);

      reader.onload = async (event) => {
        const arrayBuffer = event.target.result;
        // Convert ArrayBuffer to Uint8Array
        const uint8Array = new Uint8Array(arrayBuffer);
        // Convert Uint8Array to binary string
        let binaryString = '';
        uint8Array.forEach((byte) => {
          binaryString += String.fromCharCode(byte);
        });
        // Base64 encode the binary string
        const base64Docx = btoa(binaryString);

        // Get current SoW data
        const sowData = getExportData();

        const payload = {
          sow_data: sowData,
          template_docx_base64: base64Docx,
        };

        const response = await fetch(LAMBDA_FUNCTION_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Lambda invocation failed: ${response.status} - ${errorText}`);
        }

        const result = await response.json();
        if (result.file_content_base64) {
          // Use the utility function to convert base64 to Blob
          const blob = base64ToBlob(
            result.file_content_base64,
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
          );
          
          // Trigger download
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = result.file_name || 'generated_sow.docx';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
          alert('Document generated and downloaded successfully!');
        } else {
          throw new Error('No generated document content received from Lambda.');
        }
      };

      reader.onerror = (error) => {
        throw new Error('Error reading template file: ' + error);
      };

    } catch (error) {
      alert('Error generating document: ' + error.message);
      console.error("Document generation error:", error);
    } finally {
      stopLoading();
    }
  };

  // New: Handlers for modal
  const handleExpandRequest = (key) => {
    setEditingFieldKey(key);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingFieldKey(null);
  };

  const handleModalSave = (newValue) => {
    handleFieldChange(editingFieldKey, newValue); // Update the form data
    setIsModalOpen(false);
    setEditingFieldKey(null);
  };

  // Get current field data for the modal
  const modalFieldLabel = editingFieldKey ? editingFieldKey.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase()) : '';
  const modalInitialValue = editingFieldKey ? formData[editingFieldKey].value : '';

  const currentTabConfig = TABS_CONFIG.find(tab => tab.id === activeTab);
  const fieldsToShow = currentTabConfig ? currentTabConfig.fields : [];

  return (
    <div className={styles.soWEditor}>
      <h1>Statement of Work Editor</h1>

      {isLoading && <div className={styles.loadingIndicator}>Loading...</div>}

      <ActionBar
        onImportJson={handleImport}
        onExportJson={handleExport}
        onGenerateDocument={handleGenerateDocument}
        onTemplateFileSelect={handleTemplateFileSelect}
        onPreview={handlePreview} // Pass preview handler
        isLoading={isLoading}
      />

      {selectedTemplateFile && (
        <p className={styles.selectedFileDisplay}>
          Selected Template: <strong>{selectedTemplateFile.name}</strong>
        </p>
      )}

      <div className={styles.tabsContainer}>
        {TABS_CONFIG.map(tab => (
          <button
            key={tab.id}
            className={`${styles.tabButton} ${activeTab === tab.id ? styles.activeTab : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className={styles.formGrid}>
        {fieldsToShow.map((key) => {
          const field = formData[key];
          if (!field) return null; // Should not happen if TABS_CONFIG matches details.json keys
          return (
            <FormField
              key={key}
              label={key.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase())}
              value={field.value}
              locked={field.locked}
              onValueChange={(newValue) => handleFieldChange(key, newValue)}
              onLockToggle={() => handleLockToggle(key)}
              onExpandRequest={() => handleExpandRequest(key)} // Pass expand request handler
            />
          );
        })}
      </div>

      {isModalOpen && (
        <ExpandableModal
          label={modalFieldLabel}
          initialValue={modalInitialValue}
          isOpen={isModalOpen}
          onClose={handleModalClose}
          onSave={handleModalSave}
        />
      )}

      <PreviewModal
        isOpen={isPreviewModalOpen}
        onClose={() => setIsPreviewModalOpen(false)}
        markdownContent={markdownContent}
      />
    </div>
  );
};

export default SoWEditor;